import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {AuthService} from '../../services/auth/auth.service';
import {FiscalService} from '../../services/fiscal/fiscal.service';
import {CartService} from '../../services/cart/cart.service';
import {Router} from '@angular/router';
import {UtilService} from '../../services/util/util.service';
import {Permission} from '../../generated/sales-terminal-client';
import {Subscription, ReplaySubject} from 'rxjs';
import {ScrapService} from '../../services/scrap/scrap.service';
import {SnackService} from "../../services/snack/snack.service";
import {CartTypes} from '../../services/cart/cart-base.service';
import {RouteService} from "../../services/route/route.service";

@Component({
  selector: 'app-salespoint-role',
  templateUrl: './salespoint-role.component.html',
  styleUrls: ['./salespoint-role.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SalespointRoleComponent implements OnInit, OnDestroy {
  public currentSalespointId: number = this.authService.salespointId;
  public currentRoleId: number = this.authService.roleId;
  public showAliasSalespointSelection = false;
  public changes: boolean;

  private _subscriptions: Subscription[] = [];
  public filteredPrimarySalespoints: ReplaySubject<Permission[]> = new ReplaySubject<Permission[]>(1);
  public filteredAliasSalespoints: ReplaySubject<Permission[]> = new ReplaySubject<Permission[]>(1);
  public permissionRolesFiltered: ReplaySubject<Permission[]> = new ReplaySubject<Permission[]>(1);

  constructor(public authService: AuthService,
              private fiscalService: FiscalService,
              private cartService: CartService,
              private scrapService: ScrapService,
              private router: Router,
              private utilService: UtilService,
              private routeService: RouteService,
              private snackService: SnackService) {}

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  ngOnInit() {
    this.showAliasSalespointSelection = !!this.authService.aliasSalespoints.find(sp => sp.salespointId === this.authService.salespointId);
  }

  get permissionRoles(): Permission[] {
    return this.authService.permissions.filter(p => (p.salespointId === this.currentSalespointId));
  }

  suggestSalespoint(): void {
    this.currentSalespointId = null;
    if (this.showAliasSalespointSelection && this.authService.aliasSalespoints.length) {
      if (this.authService.aliasSalespoints.find(salespoint => salespoint.salespointId === this.authService.salespointId)) {
        this.resetChanges(false);
        return;
      } else if (this.authService.aliasSalespoints.length === 1) {
        this.currentSalespointId = this.authService.aliasSalespoints[0].salespointId;
      }
    } else if (!this.showAliasSalespointSelection && this.authService.primarySalespoints.length) {
      if (this.authService.primarySalespoints.find(salespoint => salespoint.salespointId === this.authService.salespointId)) {
        this.resetChanges(false);
        return;
      } else if (this.authService.primarySalespoints.length === 1) {
        this.currentSalespointId = this.authService.primarySalespoints[0].salespointId;
      }
    }
    this.suggestRole();
  }

  suggestRole(): void {
    this.changes = true;
    this.currentRoleId = null;
    this.permissionRolesFiltered.next(this.permissionRoles);
    if (this.permissionRoles.length === 1) {
      this.currentRoleId = this.permissionRoles[0].role.id;
    }
  }

  getSalespointLabelById(salespointId: number, salespoints: Permission[]): string {
    const salespoint = salespoints.find(salespoint => salespoint.salespointId === salespointId);
    return salespoint ? this.getSalespointLabel(salespoint) : null;
  }

  getRoleLabelById(roleId: number): string {
    return this.permissionRoles.find(role => role.role.id === roleId)?.role.name || null;
  }

  getSalespointLabel(salespoint: Permission): string {
    let name = salespoint.salespointName;
    if (salespoint.legalPerson?.companyName && salespoint.salespointName !== salespoint.legalPerson?.companyName) {
      name += ' (' + salespoint.legalPerson?.companyName + ')';
    }
    if (salespoint.legalPerson?.isPlaceholder) {
      name = this.authService.user.firstName + ' ' + this.authService.user.lastName;
    }
    return name;
  }

  public switchRole(disableRoleSwitching?: boolean): void {
    this.fiscalService.removeToken();
    this.scrapService.clearScrap();
    this.snackService.clearAll();
    if (this.authService.canAccessSales) {
      if (this.cartService.cartType !== CartTypes.SelfService && this.authService.user.supportsYbo && this.authService.salespointId !== this.currentSalespointId) {
        this.cartService.emptyLegacyCart();
      } else {
        this.cartService.clearUserCart();
      }
    }
    const changeSub = this.authService.changeRole(this.currentSalespointId, this.currentRoleId).subscribe(() => {
      this.authService.clearSalespointCache();
      if (this.authService.checkSalespointProperty('fiscal_procedures_by')) {
        this.fiscalService.getShiftStatus();
      }
      if (this.authService.redirectUrl) {
        this.routeService.navigateWithQueryParams(this.authService.redirectUrl).then(() => {
          this.authService.redirectUrl = '';
        });
      } else if (disableRoleSwitching) {
        this.authService.navigateToBaseUrl();
      } else {
        this.router.navigate(['/role-switching']);
      }
      this.utilService.userRoleSwitched.emit();
      this.resetChanges();
    });
    this._subscriptions.push(changeSub);
  }

  get isValid(): boolean {
    return !!this.currentSalespointId && !!this.currentRoleId;
  }

  get hasChanged(): boolean {
    return this.changes;
  }

  resetChanges(resetAlias = true): void {
    this.changes = false;
    this.currentSalespointId = this.authService.salespointId;
    this.currentRoleId = this.authService.roleId;
    if (resetAlias) {
      this.showAliasSalespointSelection = !!this.authService.aliasSalespoints.find(sp => sp.salespointId === this.authService.salespointId);
    }
  }
}
